import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { getBrowserVersion, querySoftwareSchema } from "@utils/index";
import { QuerySoftwareFormInterface, FileModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useQuerySoftware } from "@providers/index";
import { useNotify } from "@hooks/index";
import { attachmentsService } from "@services/index";
import {
  Form,
  FormRow,
  FormText,
  FormFile,
  FileItem,
  Button,
  InfoCircleSvg,
} from "@components/index";

export const NewQuerySoftware = ({
  onBack,
  onClose,
}: {
  onBack?: () => void;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const phone = useUserStore(({ phone }) => phone);
  const userId = useUserStore(({ userId }) => userId);
  const [browserVersion] = useState(getBrowserVersion);
  const [files, setFiles] = useState<FileModel[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const { mutate: mutateQuerySoftware, isPending: isPendingQuerySoftware } =
    useQuerySoftware(
      () => {
        reset();
        setFiles([]);
        onClose();
        notify("success", "Your message has been submitted.");
        setState({ isLoading: false });
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "phone" | "message" | "userId" | "browserVersion", {
            message: fields![key],
          }),
        );
        notify("error", message);
        setState({ isLoading: false });
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingQuerySoftware });
  }, [isPendingQuerySoftware, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    getValues,
    setError,
  } = useForm<QuerySoftwareFormInterface>({
    mode: "onBlur",
    disabled: isPendingQuerySoftware,
    resolver: yupResolver(querySoftwareSchema),
  });

  const handleCancel = () => {
    reset();
    setFiles([]);
    if (onBack) {
      onBack();
    }
  };

  const handleSelectFiles = async (target: HTMLInputElement) => {
    setIsUploadingFiles(true);
    if (!target.files?.length) {
      setIsUploadingFiles(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploadingFiles(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));
    setFiles((prev) => [...prev, ...newFilesWithSizes]);
    setIsUploadingFiles(false);
  };

  const removeFile = (uuid: string) => {
    setFiles((prev) => prev.filter((file) => file.uuid !== uuid));
  };

  const onSubmit = () => {
    const data = getValues();
    mutateQuerySoftware({ ...data, files });
  };

  return (
    <Form
      className="flex flex-col text-black-100"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex gap-x-2 rounded-lg bg-black-4 px-3 py-2.5">
        <div className="*:size-5">
          <InfoCircleSvg />
        </div>
        <div className="text-200 text-black-54">
          Software issues are sent to our <strong>tech support team</strong>,
          not your accountant — so they won't appear in your queries. We'll
          follow up with you directly if needed.
        </div>
      </div>
      <input
        type="hidden"
        value={browserVersion}
        {...register<"browserVersion">("browserVersion")}
      />
      <input type="hidden" value={phone} {...register<"phone">("phone")} />
      <input type="hidden" value={userId} {...register<"userId">("userId")} />
      <FormRow top="small">
        <FormText
          {...register<"message">("message")}
          name="message"
          label="Describe the issue"
          isSimpleLabel
          placeholder="What happened? What were you trying to do?"
          error={formState.errors.message?.message}
          onChange={() => clearErrors("message")}
        />
      </FormRow>
      <FormRow top="small">
        <FormFile
          name="files"
          label="Attach files"
          dataTestid="input_files"
          uploading={isUploadingFiles}
          disabled={isPendingQuerySoftware}
          onChange={(event) => {
            handleSelectFiles(event.target as HTMLInputElement);
            event.preventDefault();
          }}
        />
        {!!files.length && (
          <div className="mt-4 flex flex-col gap-y-2">
            {files.map((file) => (
              <FileItem key={file.uuid} file={file} onRemove={removeFile} />
            ))}
          </div>
        )}
      </FormRow>
      <div className="flex flex-col gap-y-2 py-6 md:flex-row md:justify-between">
        <Button
          type="submit"
          title="Submit"
          height="small"
          disabled={isUploadingFiles}
        />
        {onBack && (
          <Button
            variant="outline"
            title="Back"
            height="small"
            onClick={handleCancel}
          />
        )}
      </div>
    </Form>
  );
};
